import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import axios from 'axios';
import produce from 'immer';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../apiConfig';
import BillingInformation from './BillingInformation';
import BusinessInformation from './BusinessInformation';
import ContactsInformation from './ContactsInformation';
import { initialFormData, useFormContext } from './FormContext';
import ShippingInformation from './ShippingInformation';
import { validateFields } from './ValidateFunction';
import { BillingInfo, BusinessInfo, ContactType, ShippingInfo } from './types';

const MainContent: React.FC = () => {
  const { formData, activeTab, setActiveTab, setFormData } = useFormContext();
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [businessInfo, setBusinessInfo] = useState(formData.businessInfo);
  const [shippingInfo, setShippingInfo] = useState(formData.shippingInfo);
  const [billingInfo, setBillingInfo] = useState(formData.billingInfo);
  const [contactInfo, setContactInfo] = useState(formData.contactInfo);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const navigate = useNavigate();
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(
    null
  );

  const handleSetFullAddress = (fullAddress: {
    address: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
  }) => {
    handleChange('address', fullAddress.address);
    handleChange('address2', fullAddress.address2);
    handleChange('city', fullAddress.city);
    handleChange('state', fullAddress.state);
    handleChange('zip', fullAddress.zip);
  };

  const changeBusinessInfo = (field: keyof BusinessInfo, value: any) => {
    setFormData((prevFormData) => {
      const updatedBusinessInfo = {
        ...prevFormData.businessInfo,
        [field]: value,
      };
      setBusinessInfo((prevInfo) => ({
        ...prevInfo,
        [field]: value,
      }));
      return {
        ...prevFormData,
        businessInfo: updatedBusinessInfo,
      };
    });
  };

  const changeShippingInfo = (
    field: keyof ShippingInfo | keyof ContactType,
    value: any
  ) => {
    setFormData((prevFormData) => {
      const updatedShippingInfo = {
        ...prevFormData.shippingInfo,
        contact: {
          ...formData.shippingInfo.contact,
          ...(field in formData.shippingInfo.contact ? { [field]: value } : {}),
        },
        [field]: value,
      };
      setShippingInfo((prevState) =>
        produce(prevState, (draft) => {
          if (field in draft.contact) {
            (draft.contact as any)[field as keyof ContactType] = value;
          } else {
            (draft[field as keyof ShippingInfo] as any) = value;
          }
        })
      );
      return {
        ...prevFormData,
        shippingInfo: updatedShippingInfo,
      };
    });
  };

  const changeBillingInfo = (
    field: keyof BillingInfo | keyof ContactType,
    value: any
  ) => {
    setFormData((prevFormData) => {
      const updatedBillingInfo = {
        ...prevFormData.billingInfo,
        contact: {
          ...formData.billingInfo.contact,
          ...(field in formData.billingInfo.contact ? { [field]: value } : {}),
        },
        [field]: value,
      };
      setBillingInfo((prevState) =>
        produce(prevState, (draft) => {
          if (field in draft.contact) {
            (draft.contact as any)[field as keyof ContactType] = value;
          } else {
            (draft[field as keyof BillingInfo] as any) = value;
          }
        })
      );
      return {
        ...prevFormData,
        billingInfo: updatedBillingInfo,
      };
    });
  };

  const changeContactInfo = (
    index: number,
    field: keyof ContactType,
    value: any
  ) => {
    setFormData((prevFormData) => {
      const updatedContacts = prevFormData.contactInfo.contacts.map(
        (contact, i) => {
          if (i === index) {
            return { ...contact, [field]: value };
          }
          return contact;
        }
      );
      const updatedContactInfo = { contacts: updatedContacts };
      setContactInfo(updatedContactInfo);

      return {
        ...prevFormData,
        contactInfo: updatedContactInfo,
      };
    });
  };

  const handleChange = (field: string, value: any, index?: number) => {
    if (activeTab === 'business-info') {
      changeBusinessInfo(field as keyof BusinessInfo, value);
      setErrors((prevErrors: any) => ({
        ...prevErrors,
        [field]: value ? undefined : prevErrors[field],
      }));
    } else if (activeTab === 'shipping-info') {
      changeShippingInfo(field as keyof ShippingInfo, value);
    } else if (activeTab === 'billing-info') {
      changeBillingInfo(field as keyof BillingInfo, value);
    } else if (activeTab === 'contacts' && index !== undefined) {
      changeContactInfo(index, field as keyof ContactType, value);
    }
  };

  const handleSaveAndContinue = () => {
    const newErrors = validateFields(activeTab, {
      businessInfo,
      shippingInfo,
      billingInfo,
      contactInfo,
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    switch (activeTab) {
      case 'business-info':
        setActiveTab('shipping-info');
        break;
      case 'shipping-info':
        setActiveTab('billing-info');
        break;
      case 'billing-info':
        setActiveTab('contacts');
        break;
      default:
        setActiveTab('business-info');
    }
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    const newErrors = validateFields(activeTab, {
      businessInfo,
      shippingInfo,
      billingInfo,
      contactInfo,
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setLoading(true);
    setSubmitErrorMessage(null);

    try {
      const form = new FormData();
      form.append('customerJson', JSON.stringify(formData));

      const taxExemptFile = formData.billingInfo.taxExemptFile;
      if (formData.billingInfo.isTaxExempt && taxExemptFile) {
        form.append('taxExemptFile', taxExemptFile);
      }

      await api.post(`/api/customers/add`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      navigate('/customers/success');
      setFormData(initialFormData);
      setActiveTab('business-info');
    } catch (error: any) {
      console.error('Submit failed:', error);
      setSubmitError(true);

      if (axios.isAxiosError(error)) {
        const data = error.response?.data;
        let message: string;
        if (typeof data === 'string') message = data;
        else if (data?.errors) {
          const errorMessages = Object.values(data.errors).flat();
          message = errorMessages.join('\n');
        } else {
          message = 'Something went wrong. Please try again.';
        }

        setSubmitErrorMessage(message);
      }
    }
    setLoading(false);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'business-info':
        return (
          <BusinessInformation
            data={businessInfo}
            onChange={handleChange}
            errors={errors}
            handleSetFullAddress={handleSetFullAddress}
          />
        );
      case 'shipping-info':
        return (
          <ShippingInformation
            data={shippingInfo}
            businessInfo={businessInfo}
            onChange={handleChange}
            errors={errors}
            handleSetFullAddress={handleSetFullAddress}
          />
        );
      case 'billing-info':
        return (
          <BillingInformation
            data={billingInfo}
            onChange={handleChange}
            errors={errors}
            handleSetFullAddress={handleSetFullAddress}
          />
        );
      case 'contacts':
        return (
          <ContactsInformation
            data={contactInfo}
            onChange={handleChange}
            errors={errors}
          />
        );
      default:
        return (
          <BusinessInformation
            data={businessInfo}
            onChange={handleChange}
            errors={errors}
            handleSetFullAddress={handleSetFullAddress}
          />
        );
    }
  };
  const handleGoBack = () => {
    if (activeTab === 'shipping-info') {
      setActiveTab('business-info');
    } else if (activeTab === 'billing-info') {
      setActiveTab('shipping-info');
    } else if (activeTab === 'contacts') {
      setActiveTab('billing-info');
    }
  };
  return (
    <div>
      {renderTabContent()}
      <Stack width={'60%'}>
        <Stack
          justifyContent={'flex-end'}
          direction={'row'}
          marginTop={4}
          spacing={2}
        >
          {activeTab !== 'business-info' && (
            <Button variant='outlined' onClick={handleGoBack}>
              Go Back
            </Button>
          )}
          <Button
            onClick={
              activeTab === 'contacts' ? handleSubmit : handleSaveAndContinue
            }
            variant='contained'
            disabled={activeTab === 'contacts' && loading}
            startIcon={
              activeTab === 'contacts' && loading ? (
                <CircularProgress size={20} color='inherit' />
              ) : null
            }
          >
            {activeTab === 'contacts' ? 'Submit' : 'Continue'}
          </Button>
        </Stack>
      </Stack>

      {activeTab === 'contacts' &&
        submitError &&
        typeof submitErrorMessage === 'string' && (
          <Stack justifyContent='center' alignItems='center' mt={3}>
            {submitErrorMessage.split('\n').map((line, index) => (
              <Typography
                key={index}
                color='error'
                variant={index === 0 ? 'h6' : 'body2'}
                fontWeight={index === 0 ? 600 : 400}
                align='center'
              >
                {line}
              </Typography>
            ))}
          </Stack>
        )}
    </div>
  );
};

export default MainContent;
